import { useState, useEffect, useRef } from "react";
import { getGoals, saveGoals } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import TeamManagement from "@/components/TeamManagement";
import { 
  Save, 
  Target,
  Bug,
  Droplets,
  Syringe,
  HandMetal,
  ShieldCheck,
  Loader2,
  Info,
  Upload,
  Image,
  Trash2,
  CreditCard,
  ExternalLink,
  CheckCircle2,
  Crown
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const YEARS = [2024, 2025, 2026, 2027, 2028];

export default function Settings() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [goals, setGoals] = useState({
    cauti_rate: 1.3,
    suti_rate: 2.0,
    abuti_rate: 0.05,
    clabsi_rate: 0.0,
    primary_bsi_rate: 0.0,
    skin_soft_tissue_rate: 0.1,
    pneumonia_rate: 0.2,
    cdiff_rate: 2.55,
    mdro_rate: 1.0,
    total_hai_rate: 2.0,
    ho_mdro_labid_rate: 1.0,
    ho_cdi_labid_rate: 2.55,
    urinary_catheter_utilization: 0.10,
    central_line_utilization: 0.10,
    hand_hygiene_compliance: 90.0,
    isolation_ppe_compliance: 95.0,
    ic_eoc_compliance: 92.0,
    dialysis_eoc_compliance: 90.0,
  });

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      try {
        const result = await getGoals(year);
        if (result) {
          setGoals({
            cauti_rate: result.cauti_rate ?? 1.3,
            suti_rate: result.suti_rate ?? 2.0,
            abuti_rate: result.abuti_rate ?? 0.05,
            clabsi_rate: result.clabsi_rate ?? 0.0,
            primary_bsi_rate: result.primary_bsi_rate ?? 0.0,
            skin_soft_tissue_rate: result.skin_soft_tissue_rate ?? 0.1,
            pneumonia_rate: result.pneumonia_rate ?? 0.2,
            cdiff_rate: result.cdiff_rate ?? 2.55,
            mdro_rate: result.mdro_rate ?? 1.0,
            total_hai_rate: result.total_hai_rate ?? 2.0,
            ho_mdro_labid_rate: result.ho_mdro_labid_rate ?? 1.0,
            ho_cdi_labid_rate: result.ho_cdi_labid_rate ?? 2.55,
            urinary_catheter_utilization: result.urinary_catheter_utilization ?? 0.10,
            central_line_utilization: result.central_line_utilization ?? 0.10,
            hand_hygiene_compliance: result.hand_hygiene_compliance ?? 90.0,
            isolation_ppe_compliance: result.isolation_ppe_compliance ?? 95.0,
            ic_eoc_compliance: result.ic_eoc_compliance ?? 92.0,
            dialysis_eoc_compliance: result.dialysis_eoc_compliance ?? 90.0,
          });
        }
      } catch (error) {
        console.error("Failed to fetch goals:", error);
      }
      setLoading(false);
    };
    fetchGoals();
  }, [year]);

  // Fetch logo on mount
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/logo`);
        const data = await res.json();
        if (data.exists && data.data) {
          setLogoUrl(`data:${data.content_type};base64,${data.data}`);
        }
      } catch (error) {
        console.error("Failed to fetch logo:", error);
      }
    };
    fetchLogo();
  }, []);

  // Fetch subscription on mount
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await fetch(`${API_URL}/api/organization`, {
          credentials: "include"
        });
        const data = await res.json();
        if (data && data.subscription) {
          setSubscription(data.subscription);
        }
      } catch (error) {
        console.error("Failed to fetch subscription:", error);
      }
    };
    fetchSubscription();
  }, []);

  const handleCheckout = async (lookupKey) => {
    setCheckoutLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          lookup_key: lookupKey,
          origin_url: window.location.origin
        })
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout");
    }
    setCheckoutLoading(false);
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });
      const data = await res.json();
      if (data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        toast.error("Failed to open billing portal");
      }
    } catch (error) {
      console.error("Portal error:", error);
      toast.error("Failed to open billing portal");
    }
    setPortalLoading(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setLogoLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/settings/logo`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        // Re-fetch logo
        const logoRes = await fetch(`${API_URL}/api/settings/logo`);
        const data = await logoRes.json();
        if (data.exists && data.data) {
          setLogoUrl(`data:${data.content_type};base64,${data.data}`);
        }
        toast.success("Logo uploaded successfully");
        // Trigger a page reload to update sidebar
        window.location.reload();
      } else {
        toast.error("Failed to upload logo");
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Failed to upload logo");
    }
    setLogoLoading(false);
  };

  const handleLogoDelete = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/logo`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setLogoUrl(null);
        toast.success("Logo removed");
        window.location.reload();
      }
    } catch (error) {
      toast.error("Failed to remove logo");
    }
  };

  const handleChange = (field, value) => {
    setGoals((prev) => ({
      ...prev,
      [field]: parseFloat(value) || 0,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveGoals({
        year,
        ...goals,
      });
      toast.success(`Goals saved for ${year}`);
    } catch (error) {
      console.error("Failed to save goals:", error);
      toast.error("Failed to save goals");
    }
    setSaving(false);
  };

  const GoalInput = ({ label, field, unit = "", helpText }) => (
    <div className="space-y-1.5">
      <Label htmlFor={field} className="text-xs font-medium text-slate-600">
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={field}
          type="number"
          step="0.01"
          min="0"
          value={goals[field]}
          onChange={(e) => handleChange(field, e.target.value)}
          className="h-9 font-mono text-sm"
          data-testid={`goal-${field}`}
        />
        {unit && <span className="text-xs text-slate-500 whitespace-nowrap">{unit}</span>}
      </div>
      {helpText && <p className="text-xs text-slate-400">{helpText}</p>}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure yearly goals and compliance targets
          </p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28" data-testid="settings-year-selector">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {YEARS.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Info Banner */}
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-sky-800 font-medium">Year-Specific Goals</p>
          <p className="text-sm text-sky-700 mt-1">
            Goals can be configured separately for each year. Changes to {year} goals will not affect other years.
            These targets are based on NHSN benchmarks and can be adjusted to meet your facility's requirements.
          </p>
        </div>
      </div>

      {/* Logo Upload Section */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
          <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
            <Image className="w-4 h-4" />
            Organization Logo
          </CardTitle>
          <CardDescription className="text-xs">
            Upload your facility logo to display in the sidebar and documents
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex items-center gap-6">
            {/* Logo Preview */}
            <div className="w-32 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="Organization Logo" className="max-w-full max-h-full object-contain p-2" />
              ) : (
                <div className="text-center">
                  <Image className="w-8 h-8 text-slate-300 mx-auto" />
                  <p className="text-xs text-slate-400 mt-1">No logo</p>
                </div>
              )}
            </div>
            
            {/* Upload Controls */}
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-upload-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={logoLoading}
                className="flex items-center gap-2"
                data-testid="upload-logo-btn"
              >
                {logoLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {logoUrl ? 'Change Logo' : 'Upload Logo'}
              </Button>
              {logoUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleLogoDelete}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="delete-logo-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove
                </Button>
              )}
              <p className="text-xs text-slate-500">
                PNG, JPG or SVG. Max 2MB recommended.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Section */}
      <Card className="border-slate-200 shadow-sm" data-testid="subscription-card">
        <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
          <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
            <CreditCard className="w-4 h-4" />
            Subscription
          </CardTitle>
          <CardDescription className="text-xs">
            Manage your IP Genius subscription plan
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Current Plan Display */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Current Plan</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-lg font-bold text-slate-900 capitalize">
                    {subscription?.plan || "Trial"}
                  </span>
                  {subscription?.plan === "enterprise" && (
                    <Crown className="w-5 h-5 text-amber-500" />
                  )}
                  <Badge 
                    variant={subscription?.status === "active" ? "default" : "secondary"}
                    className={subscription?.status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : ""}
                  >
                    {subscription?.status || "active"}
                  </Badge>
                </div>
              </div>
              {subscription?.stripe_subscription_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  data-testid="manage-subscription-btn"
                >
                  {portalLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ExternalLink className="w-4 h-4 mr-2" />
                  )}
                  Manage Billing
                </Button>
              )}
            </div>
          </div>

          {/* Plan Options (show if on trial or starter) */}
          {(!subscription?.plan || subscription?.plan === "trial" || subscription?.plan === "starter") && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-4">Upgrade Your Plan</h4>
              <div className="grid md:grid-cols-3 gap-4">
                {/* Essentials */}
                <div className={`p-4 rounded-lg border-2 ${subscription?.plan === "starter" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
                  <h5 className="font-semibold text-slate-900">Essentials</h5>
                  <p className="text-2xl font-bold text-slate-900 mt-2">$49<span className="text-sm font-normal text-slate-500">/mo</span></p>
                  <ul className="mt-3 space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> 5 team members</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> 500 docs/month</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Basic scorecard</li>
                  </ul>
                  <Button
                    className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleCheckout("essentials_monthly")}
                    disabled={checkoutLoading || subscription?.plan === "starter"}
                    data-testid="upgrade-essentials-btn"
                  >
                    {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                     subscription?.plan === "starter" ? "Current Plan" : "Upgrade"}
                  </Button>
                </div>

                {/* Professional */}
                <div className="p-4 rounded-lg border-2 border-emerald-500 bg-emerald-50 relative">
                  <Badge className="absolute -top-2 right-4 bg-emerald-600">Popular</Badge>
                  <h5 className="font-semibold text-slate-900">Professional</h5>
                  <p className="text-2xl font-bold text-slate-900 mt-2">$99<span className="text-sm font-normal text-slate-500">/mo</span></p>
                  <ul className="mt-3 space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> 20 team members</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> 2,000 docs/month</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Mock surveys</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Live IP chat</li>
                  </ul>
                  <Button
                    className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleCheckout("professional_monthly")}
                    disabled={checkoutLoading || subscription?.plan === "professional"}
                    data-testid="upgrade-professional-btn"
                  >
                    {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                     subscription?.plan === "professional" ? "Current Plan" : "Upgrade"}
                  </Button>
                </div>

                {/* Enterprise */}
                <div className="p-4 rounded-lg border-2 border-slate-200">
                  <h5 className="font-semibold text-slate-900">Enterprise</h5>
                  <p className="text-2xl font-bold text-slate-900 mt-2">$249<span className="text-sm font-normal text-slate-500">/mo</span></p>
                  <ul className="mt-3 space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Unlimited members</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Unlimited docs</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> API access</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Dedicated support</li>
                  </ul>
                  <Button
                    className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleCheckout("enterprise_monthly")}
                    disabled={checkoutLoading || subscription?.plan === "enterprise"}
                    data-testid="upgrade-enterprise-btn"
                  >
                    {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                     subscription?.plan === "enterprise" ? "Current Plan" : "Upgrade"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Team Management */}
      <TeamManagement />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Infection Rate Goals */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
                <Bug className="w-4 h-4" />
                Infection Rate Goals
              </CardTitle>
              <CardDescription className="text-xs">
                Maximum acceptable rates (lower is better)
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <GoalInput 
                  label="CAUTI Rate" 
                  field="cauti_rate" 
                  unit="/1K cath days"
                  helpText="Catheter-Associated UTI"
                />
                <GoalInput 
                  label="SUTI Rate" 
                  field="suti_rate" 
                  unit="/1K pt days"
                  helpText="Non-catheter UTI"
                />
                <GoalInput 
                  label="ABUTI Rate" 
                  field="abuti_rate" 
                  unit="/1K pt days"
                  helpText="Asymptomatic Bacteremic UTI"
                />
                <GoalInput 
                  label="CLABSI Rate" 
                  field="clabsi_rate" 
                  unit="/1K line days"
                  helpText="Central Line BSI"
                />
                <GoalInput 
                  label="Primary BSI Rate" 
                  field="primary_bsi_rate" 
                  unit="/1K pt days"
                />
                <GoalInput 
                  label="Skin/Soft Tissue Rate" 
                  field="skin_soft_tissue_rate" 
                  unit="/1K pt days"
                />
                <GoalInput 
                  label="Pneumonia Rate" 
                  field="pneumonia_rate" 
                  unit="/1K pt days"
                />
                <GoalInput 
                  label="C.diff Rate" 
                  field="cdiff_rate" 
                  unit="/10K pt days"
                />
                <GoalInput 
                  label="MDRO Rate" 
                  field="mdro_rate" 
                  unit="/1K pt days"
                />
                <GoalInput 
                  label="Total HAI Rate" 
                  field="total_hai_rate" 
                  unit="/1K pt days"
                />
              </div>
            </CardContent>
          </Card>

          {/* LabID Event Goals */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
                <Bug className="w-4 h-4" />
                Hospital-Onset LabID Goals
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GoalInput 
                  label="HO-MDRO LabID Rate" 
                  field="ho_mdro_labid_rate" 
                  unit="/1K pt days"
                />
                <GoalInput 
                  label="HO-CDI LabID Rate" 
                  field="ho_cdi_labid_rate" 
                  unit="/10K pt days"
                />
              </div>
            </CardContent>
          </Card>

          {/* Device Utilization Goals */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
                <Syringe className="w-4 h-4" />
                Device Utilization Goals
              </CardTitle>
              <CardDescription className="text-xs">
                Maximum acceptable utilization ratios (lower is better)
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GoalInput 
                  label="Urinary Catheter Utilization" 
                  field="urinary_catheter_utilization" 
                  unit="ratio"
                  helpText="Catheter days / Patient days"
                />
                <GoalInput 
                  label="Central Line Utilization" 
                  field="central_line_utilization" 
                  unit="ratio"
                  helpText="Line days / Patient days"
                />
              </div>
            </CardContent>
          </Card>

          {/* Compliance Goals */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
                <ShieldCheck className="w-4 h-4" />
                Compliance Goals
              </CardTitle>
              <CardDescription className="text-xs">
                Minimum acceptable compliance rates (higher is better)
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GoalInput 
                  label="Hand Hygiene Compliance" 
                  field="hand_hygiene_compliance" 
                  unit="%"
                  helpText="Target: ≥90%"
                />
                <GoalInput 
                  label="Isolation PPE Compliance" 
                  field="isolation_ppe_compliance" 
                  unit="%"
                  helpText="Target: ≥95%"
                />
                <GoalInput 
                  label="IC EOC Compliance" 
                  field="ic_eoc_compliance" 
                  unit="%"
                  helpText="Environment of Care rounds"
                />
                <GoalInput 
                  label="Dialysis EOC Compliance" 
                  field="dialysis_eoc_compliance" 
                  unit="%"
                  helpText="Dialysis environment rounds"
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-4 sticky bottom-4 bg-slate-50 p-4 -mx-4 rounded-lg border border-slate-200 shadow-lg">
            <Button
              type="submit"
              disabled={saving}
              className="bg-sky-600 hover:bg-sky-700 text-white shadow-sm px-8"
              data-testid="save-goals-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save {year} Goals
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
